import React, { useState } from 'react';
import styles from './login-bubble.module.scss';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { isNormalUser } from 'shared/store/selectors/user.selector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';
import { Modal } from 'antd';
import { OTPConfirm } from '../../application/switchOrganization/otp-confirmation-modal.component';
import analyticsService from 'src/helpers/analytics.service';
import { AUTHENTICATION_TYPE, EVENTS } from 'shared/constants';
import userService from 'shared/services/user.service';
import { getSignInFlowErrorMessage } from 'shared/hooks/auth.hooks';
import { useSignInFlow } from '../hooks';
import { IconButton } from 'src/components/design-system/buttons';

const REDIRECT_WATCHDOG_MS = 10_000;

export function LoginBubble() {
  const lang = useSelector(getLang('ONBOARDING'));
  const welcomeLang = useSelector(getLang('WELCOME_SCREEN'));
  const signOutCode = useSelector(state => state?.auth?.signOutCode || null);
  const isUser = useSelector(isNormalUser);
  const { startSignIn } = useSignInFlow();
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [closeBubble, setCloseBubble] = useState(false);

  if (isUser) {
    return null;
  }

  const onRootClick = () => {
    analyticsService.track(EVENTS.AUTH.LOGIN_BUBBLE_CLICKED);
  };

  const onOpenLogin = () => {
    setOtpModalOpen(true);

    analyticsService.track(EVENTS.AUTH.LOGIN_BUBBLE_LOGIN_FLOW_OPENED);
  };

  const onCloseLogin = () => {
    setOtpModalOpen(false);

    analyticsService.track(EVENTS.AUTH.LOGIN_BUBBLE_LOGIN_FLOW_CLOSED);
  };

  const onSingIn = async email => {
    let result;
    try {
      result = await startSignIn({ email });
    } catch (e) {
      const failureContext = { error: 'UNEXPECTED', email, signOutCode, message: e?.message };
      analyticsService.track(EVENTS.AUTH.LOGIN_BUBBLE_LOGIN_FAILED, failureContext);
      analyticsService.track(EVENTS.AUTH.LOGIN_FAILED, {
        ...failureContext,
        entryPoint: 'login-bubble',
      });
      return { error: welcomeLang.FAILED_TO_AUTHENTICATION };
    }

    if (result.error) {
      const failureContext = {
        error: result.error,
        email,
        signOutCode,
        authType: result.authType,
      };
      analyticsService.track(EVENTS.AUTH.LOGIN_BUBBLE_LOGIN_FAILED, failureContext);
      analyticsService.track(EVENTS.AUTH.LOGIN_FAILED, {
        ...failureContext,
        entryPoint: 'login-bubble',
      });
      return { error: getSignInFlowErrorMessage(result.error, welcomeLang) };
    }

    // SAML / OAUTH: the hook has kicked off a full-page redirect. Hold the
    // modal in its loading state until the browser navigates away, but fall
    // back to an error after REDIRECT_WATCHDOG_MS so a blocked/failed redirect
    // doesn't trap the user.
    if (
      result.authType === AUTHENTICATION_TYPE.SAML ||
      result.authType === AUTHENTICATION_TYPE.OAUTH
    ) {
      return new Promise(resolve => {
        setTimeout(() => {
          analyticsService.track(EVENTS.AUTH.LOGIN_FAILED, {
            error: 'REDIRECT_TIMEOUT',
            email,
            authType: result.authType,
            entryPoint: 'login-bubble',
          });
          resolve({ error: welcomeLang.FAILED_TO_AUTHENTICATION });
        }, REDIRECT_WATCHDOG_MS);
      });
    }

    return { data: { id: result.userId } };
  };

  const onVerifyCode = async ({ userId, code, email }) => {
    const res = await userService.verifyCode({ code, userId });
    if (res?.error) {
      analyticsService.track(EVENTS.AUTH.LOGIN_BUBBLE_LOGIN_FAILED, {
        error: res.error,
        userId,
        code,
        email,
      });
    }
    return res;
  };

  const onFinished = async () => {
    analyticsService.track(EVENTS.AUTH.LOGIN_BUBBLE_LOGIN_SUCCESS);
    setOtpModalOpen(false);
  };

  if (closeBubble) {
    return (
      <button type="button" className={styles.rootClose} onClick={() => setCloseBubble(false)}>
        <FontAwesomeIcon icon={faRightToBracket} />
      </button>
    );
  }
  return (
    <>
      <Modal
        open={otpModalOpen}
        onCancel={onCloseLogin}
        width={700}
        footer={null}
        centered
        mask={{ closable: false }}
        destroyOnHidden>
        <OTPConfirm
          showSuccess={false}
          lang={lang}
          next={onFinished}
          submitEmailCallback={onSingIn}
          verifyCodeCallback={onVerifyCode}
        />
      </Modal>
      <div className={styles.root} onClick={onRootClick} role="presentation">
        <div className={styles.fullExperienceWrapper}>
          <IconButton
            label="Close"
            className={styles.closeIconButton}
            onClick={() => setCloseBubble(true)}>
            <FontAwesomeIcon icon={faCircleXmark} className={styles.closeIcon} />
          </IconButton>
          <p>{lang.FULL_EXPERIENCE}</p>
        </div>
        <button onClick={onOpenLogin} className={styles.loginButtonWrapper}>
          <div className={styles.loginInnerDiv}>
            <FontAwesomeIcon icon={faRightToBracket} />
            {lang.LOGIN}
          </div>
        </button>
      </div>
    </>
  );
}
